import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs';

import {
  ErrorResponse,
  OrderListItemResponse,
  PageOrderListItemResponse,
} from '@app/api/marketplace/models';
import { OrderListsService } from '@app/api/marketplace/services';
import {
  OrderListActions,
  OrderListSelectors,
} from '@app/order-list/store/order-list.slice';
import { SnackbarComponent } from '@shared/components/snackbar/snackbar.component';
import { PageResponse } from '@shared/interfaces/page-response.interface';
import { DialogService } from '@shared/services/dialog.service';

import { OffersActions } from './offers.slice';

@Injectable({ providedIn: 'root' })
export class OrderListEffects {
  loadOrderList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.loadOrderList.trigger),
      switchMap((action) =>
        this.orderListsService.getCurrentOrderListItems(action.filter).pipe(
          map((response: PageOrderListItemResponse) =>
            OrderListActions.loadOrderList.success({
              response: response as PageResponse<OrderListItemResponse>,
            })
          ),
          catchError(() => of(OrderListActions.loadOrderList.failure()))
        )
      )
    )
  );

  updateItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.updateItem.trigger),
      switchMap((action) =>
        this.orderListsService
          .updateCurrentOrderListItemQuantity({
            itemId: action.itemId,
            body: { amount: action.amount },
          })
          .pipe(
            map(() => {
              return OrderListActions.updateItem.success();
            })
          )
      )
    )
  );

  addItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.addItems.trigger),
      switchMap((action) =>
        this.orderListsService
          .addNewItemsToCurrentOrderList({ body: action.request })
          .pipe(
            map(() => {
              this.store.dispatch(OffersActions.reset());
              this.snackBar.openFromComponent(SnackbarComponent, {
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
                duration: 3000,
                data: this.translateService.instant('SHARED.ADD_SUCCESS'),
              });
              this.store.dispatch(
                OrderListActions.loadOrderList.trigger({
                  filter: { page: 0, size: 9999 },
                })
              );
              return OrderListActions.addItems.success();
            }),
            catchError((error) => {
              const errorResponse = error.error as ErrorResponse;
              this.dialogService.open({
                content: [
                  ...Object.values(
                    errorResponse.templateParameters?.[
                      'packagingUnitIdLabelPairs'
                    ] ?? {}
                  ),
                ].join('\r\n'),
                title: 'SORTIMENT.ADD_TO_CART_ERROR_DIALOG_TITLE',
                type: 'NOTICE',
              });
              return of(
                OrderListActions.addItems.failure({
                  errorIds: [
                    ...Object.keys(
                      errorResponse.templateParameters?.[
                        'packagingUnitIdLabelPairs'
                      ] ?? {}
                    ),
                  ],
                })
              );
            })
          )
      )
    )
  );

  removeItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.removeItem.trigger),
      withLatestFrom(
        this.store.select(OrderListSelectors.selectOrderListFilter)
      ),
      switchMap(([action, filter]) =>
        this.orderListsService
          .deleteItemFormCurrentOrderList({ itemId: action.id })
          .pipe(
            map(() => {
              this.store.dispatch(
                OrderListActions.loadOrderList.trigger({ filter })
              );
              return OrderListActions.removeItem.success();
            })
          )
      )
    )
  );

  removeItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.removeItems.trigger),
      withLatestFrom(
        this.store.select(OrderListSelectors.selectOrderListFilter)
      ),
      switchMap(([action, filter]) =>
        this.orderListsService
          .deleteItemsFormCurrentOrderList({
            body: { orderListItemIdList: action.request },
          })
          .pipe(
            map(() => {
              this.store.dispatch(
                OrderListActions.loadOrderList.trigger({ filter })
              );
              return OrderListActions.removeItems.success();
            })
          )
      )
    )
  );

  updateOrderListFromSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrderListActions.updateOrderListFromSearch.trigger),
      switchMap((action) =>
        this.orderListsService
          .updateOrderListItemsQuantityBulk({
            body: action.body.orderListBulk,
          })
          .pipe(
            map(() => {
              this.store.dispatch(
                OrderListActions.loadOrderList.trigger({
                  filter: { page: 0, size: 9999 },
                })
              );
              return OrderListActions.updateOrderListFromSearch.success();
            })
          )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly orderListsService: OrderListsService,
    private readonly store: Store,
    private readonly snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialogService: DialogService
  ) {}
}
